// The credential holder; the three fleet-logout doors close by construction (sdk ADR-010).
import {
  classifyVerdict,
  VERDICT_ACTIVE,
} from '../auth/authState.js';
import type {
  HolderEvent,
  HolderPhase,
  SignedOutReason,
  TransitionResult,
  CredentialMaterial,
} from '../auth/authState.js';
import type { AuthProvider } from '../auth/types.js';
import { credentialFingerprint, decodeSessionJwt } from '../auth/decodeSessionJwt.js';
import type { BridgeIdentity } from '../resources/bridge-events.js';
import {
  bearerOf,
  degradedReasonOf,
  expiresAtOf,
  holdsIdentity,
  identityOf,
  isRenewable,
} from '../lifecycle/capabilities.js';
import { LIFECYCLE_BUDGET_MS, withBudget } from '../lifecycle/budget.js';
import type { BudgetedOutcome } from '../lifecycle/budget.js';
import { emitLifecycleEvent } from '../lifecycle/observability.js';
import { HolderStore } from './holder-store.js';
import { daemonRenewal } from './daemon-arm.js';
import {
  BROWSER_REFRESH_LEAD_MS,
  DAEMON_MIRROR_INTERVAL_MS,
  decideRenewFailure,
  isRefreshDue,
} from './renew.js';

/** The host's account-scoped reclaim, awaited by the transition (sdk ADR-011). */
export type TeardownBarrier = () => Promise<void>;

/** The browser arm's renewal source, host-injected (sdk ADR-010). */
export type RenewSource = () => Promise<CredentialMaterial | null>;

export class IdentityNotConfiguredError extends Error {
  constructor() {
    super(
      'sdk.identity: no credential holder is configured on this client. ' +
        'Construct the SDK through createSDK with a credential configuration.',
    );
  }
}

export interface IdentityConfig {
  store: import('../storage/types.js').SDKStorage | null;
  /** The sync device-id projection `bridgeIdentity()` speaks as (sdk ADR-009). */
  deviceId?: () => string;
  /** The daemon arm's bearer fetch; null is never mapped to an event (sdk ADR-019). */
  daemonBearer?: () => Promise<CredentialMaterial | null>;
  daemonLogout?: () => Promise<void>;
  /** The browser arm's renewal source (the host's Stytch client). */
  renewSource?: RenewSource;
  /** Host rotation hook, required on the browser arm (sdk ADR-010). */
  adopt?: (material: CredentialMaterial) => Promise<void>;
  clockIntervalMs?: number;
  /** The browser arm's refresh lead; injectable so a test needs no fake clock (sdk ADR-018). */
  refreshLeadMs?: number;
  /** The one lifecycle budget. */
  budgetMs?: number;
  /** (sdk ADR-027) */
  realm?: string;
}

type Listener = (phase: HolderPhase) => void;

export class IdentityController {
  private readonly holder: HolderStore;
  private readonly config: IdentityConfig;
  private readonly arm: 'daemon' | 'browser';
  private readonly listeners = new Set<Listener>();
  private readonly barriers = new Set<TeardownBarrier>();
  private readonly identityChangeBarriers = new Set<TeardownBarrier>();
  /** Bumped on every arrival at `no_credential` (sdk ADR-010). */
  private signOutEpoch = 0;
  private renewInflight: Promise<unknown> | null = null;
  private renewNotBefore = 0;
  private renewAttempts = 0;
  private lastDaemonMirrorAt = 0;
  private disposed = false;
  private readonly refreshLeadMs: number;
  private readonly budgetMs: number;

  constructor(arm: 'daemon' | 'browser', config: IdentityConfig) {
    this.arm = arm;
    this.config = config;
    this.refreshLeadMs = config.refreshLeadMs ?? BROWSER_REFRESH_LEAD_MS;
    this.budgetMs = config.budgetMs ?? LIFECYCLE_BUDGET_MS;
    this.holder = new HolderStore(
      config.store,
      config.clockIntervalMs,
      () => {
        // (sdk ADR-010)
        void this.renewIfNeeded();
      },
      true,
      // The transition barrier: every route to no_credential awaits it (sdk ADR-011).
      async () => {
        this.signOutEpoch += 1;
        await this.runBarriers();
      },
      config.realm ?? null,
      {},
      // The identity-change barrier: a usable->usable re-tenant reclaims too (sdk ADR-011).
      async () => {
        await this.runIdentityChangeBarriers();
      },
    );
    this.holder.subscribe((phase) => {
      for (const fn of this.listeners) fn(phase);
    });
  }

  // ── reads ────────────────────────────────────────────────────────────────

  get phase(): HolderPhase {
    return this.holder.phase;
  }

  /** The discarded blob's cause; the host raises the warning (sdk ADR-014). */
  get restoreDiagnostic(): import('./holder-store.js').CredentialUnreadable | null {
    return this.holder.restoreDiagnostic;
  }

  get armName(): 'daemon' | 'browser' {
    return this.arm;
  }

  subscribe(fn: Listener): () => void {
    this.listeners.add(fn);
    return () => this.listeners.delete(fn);
  }

  /** The four-field identity tuple the bridge and session binding speak as.
   *  Gated on holds-identity (sdk ADR-012). */
  bridgeIdentity(): BridgeIdentity {
    const phase = this.holder.phase;
    const identity = identityOf(phase);
    const token = bearerOf(phase);
    if (!holdsIdentity(phase) || identity === null || token === null) {
      throw new Error(
        `sdk.identity.bridgeIdentity: the holder is ${phase.phase}, which holds no identity — ` +
          'no tenancy to speak as',
      );
    }
    return {
      deviceId: this.deviceIdSync(),
      organizationId: identity.org_id,
      accountId: identity.account_id,
      token,
    };
  }

  /** The bearer as a projection of the phase (sdk ADR-010, sdk ADR-012). */
  asAuthProvider(): AuthProvider {
    return {
      getToken: () => Promise.resolve(bearerOf(this.holder.phase)),
      invalidate: () => Promise.resolve(),
    };
  }

  // ── transitions ──────────────────────────────────────────────────────────

  /** The browser arm's only intake (sdk ADR-010). */
  async acquired(material: CredentialMaterial): Promise<TransitionResult> {
    const event = this.eventForAcquired(material);
    return this.holder.apply(event);
  }

  /** The user's own sign-out; machine-wide on the daemon arm (sdk ADR-010, sdk ADR-011). */
  async signOut(reason: SignedOutReason = 'user_signout'): Promise<void> {
    let unreleased: BudgetedOutcome<void> | null = null;
    if (this.arm === 'daemon' && this.config.daemonLogout) {
      // Bounded, and both budget outcomes still clear locally (sdk ADR-018, sdk ADR-021).
      const outcome = await withBudget(this.config.daemonLogout(), this.budgetMs);
      if (outcome.kind !== 'settled') {
        unreleased = outcome;
      }
    }
    await this.holder.clear(reason);
    if (unreleased === null) return;
    if (unreleased.kind === 'expired') {
      emitLifecycleEvent('lifecycle.logout_unbounded', {
        realm: this.config.realm ?? null,
        elapsed: unreleased.elapsedMs,
        budget: this.budgetMs,
        local_clear_proceeded: true,
      });
      return;
    }
    emitLifecycleEvent('lifecycle.logout_unreachable', {
      realm: this.config.realm ?? null,
      elapsed: unreleased.elapsedMs,
      cause: causeText(unreleased.cause),
      local_clear_proceeded: true,
    });
  }

  /** (sdk ADR-018) */
  isRenewalDue(now: number = Date.now()): boolean {
    const phase = this.holder.phase;
    if (!isRenewable(phase)) return false;
    if (degradedReasonOf(phase) !== null) return true;
    if (this.arm === 'daemon') {
      // A backstop on its own cadence, not the expiry tick (sdk ADR-018, sdk ADR-019).
      if (now - this.lastDaemonMirrorAt < DAEMON_MIRROR_INTERVAL_MS) return false;
      this.lastDaemonMirrorAt = now;
      return true;
    }
    const expiresAt = expiresAtOf(phase);
    if (expiresAt === null) {
      throw new Error(
        `sdk.identity: ${phase.phase} is renewable but carries no expiry — ` +
          'the capability table and the phase union disagree',
      );
    }
    return isRefreshDue(expiresAt, this.refreshLeadMs, now);
  }

  /** Single-flighted, honouring the back-off floor a failure set (sdk ADR-018). */
  async renewIfNeeded(opts?: { force?: boolean; now?: number }): Promise<TransitionResult | null> {
    const now = opts?.now ?? Date.now();
    if (!isRenewable(this.holder.phase)) {
      return null;
    }
    if (!opts?.force && !this.isRenewalDue(now)) {
      return null;
    }
    if (!opts?.force && now < this.renewNotBefore) {
      return null;
    }
    if (this.renewInflight !== null) {
      // A joiner observes the settlement; the initiator's catch owns the decision (sdk ADR-018).
      await this.renewInflight.catch(() => undefined);
      return null;
    }
    const attempt = (this.renewAttempts += 1);
    this.renewInflight = this.renew();
    try {
      await this.renewInflight;
      this.renewAttempts = 0;
      return null;
    } catch (err) {
      // The driver decides from the classifier, never message text (sdk ADR-002, sdk ADR-010).
      const decision = decideRenewFailure(err, attempt);
      const applied = await this.holder.apply(decision.event);
      if (decision.retryAfterMs > 0) {
        this.renewNotBefore = now + decision.retryAfterMs;
      }
      return applied;
    } finally {
      this.renewInflight = null;
    }
  }

  // ── the host's two hooks ─────────────────────────────────────────────────

  /** Feed one transport outcome (2xx or 401 only — see the class header). */
  async observeTransport(status: number, err?: unknown): Promise<void> {
    if (status < 300) {
      // A 2xx is authoritative and beats any local read (sdk ADR-010).
      await this.holder.apply({ type: 'VerdictObserved', verdict: VERDICT_ACTIVE, at: Date.now() });
      return;
    }
    if (status === 401) {
      const verdict = classifyVerdict(err);
      await this.holder.apply({ type: 'VerdictObserved', verdict, at: Date.now() });
      // Only an adjudicated expiry renews now (sdk ADR-018).
      if (degradedReasonOf(this.holder.phase) === 'expired') {
        await this.renewIfNeeded();
      }
    }
  }

  /** The daemon arm's `usable` intake, fingerprint-gated (sdk ADR-018, sdk ADR-019). */
  async acquireFromDaemon(): Promise<void> {
    if (!this.config.daemonBearer) {
      throw new Error('sdk.identity: acquireFromDaemon requires the daemon bearer fetch');
    }
    const epoch = this.signOutEpoch;
    const info = await this.config.daemonBearer();
    if (info === null) {
      // A null bearer is not a sign-out; the frame is the authority (sdk ADR-019).
      return;
    }
    // A sign-out that landed inside the fetch wins (sdk ADR-010).
    if (this.signOutEpoch !== epoch) {
      return;
    }
    const phase = this.holder.phase;
    if (
      phase.phase === 'usable' &&
      credentialFingerprint(info.session_jwt) ===
        credentialFingerprint(phase.material.session_jwt)
    ) {
      return;
    }
    await this.acquired(info);
  }

  /** Apply a mirror event from the daemon arm (the exhaustive table's output). */
  async applyMirror(event: HolderEvent): Promise<void> {
    await this.holder.apply(event);
  }

  /** Non-material events only; material events go through `acquired()` (sdk ADR-010). */
  async applyEvent(event: HolderEvent): Promise<TransitionResult> {
    return this.holder.apply(event);
  }

  /** A daemon `no_credential` frame; the clear is machine-wide (sdk ADR-014, sdk ADR-015). */
  async observeDaemonClear(reason: SignedOutReason): Promise<void> {
    await this.holder.observeDaemonClear(reason);
  }

  /** Register the account-scoped reclamation barrier (sdk ADR-011's registry). */
  registerTeardown(barrier: TeardownBarrier): () => void {
    this.barriers.add(barrier);
    return () => this.barriers.delete(barrier);
  }

  /** The same registry for an in-place credential swap (sdk ADR-011). */
  registerIdentityChangeTeardown(barrier: TeardownBarrier): () => void {
    this.identityChangeBarriers.add(barrier);
    return () => this.identityChangeBarriers.delete(barrier);
  }

  /** Stop timers. Idempotent; the client's lifetime owns this. */
  dispose(): void {
    this.disposed = true;
    this.holder.dispose();
  }

  // ── internals ────────────────────────────────────────────────────────────

  private deviceIdSync(): string {
    const accessor = this.config.deviceId;
    if (!accessor) {
      throw new Error(
        'sdk.identity: this controller was constructed with no deviceId accessor, so it ' +
          'cannot name the device it speaks as',
      );
    }
    return accessor();
  }

  /** Start the arm's background work. Called once by the client. */
  startArm(armWork: (controller: IdentityController) => void): void {
    if (!this.disposed) {
      armWork(this);
    }
  }

  private eventForAcquired(material: CredentialMaterial): HolderEvent {
    // The identity is read off the material by the one decoder (sdk ADR-004, sdk ADR-012).
    const claims = decodeSessionJwt(material.session_jwt);
    if (claims.org_id === null || claims.account_id === null) {
      return { type: 'CredentialAcquiredUnactivated', material };
    }
    if (claims.exp === null) {
      throw new Error('sdk.identity: the session JWT carries no exp claim');
    }
    return {
      type: 'CredentialAcquired',
      material,
      identity: {
        org_id: claims.org_id,
        account_id: claims.account_id,
        user_id: claims.user_id ?? '',
        email: claims.email ?? '',
      },
      expires_at: claims.exp,
    };
  }

  private async runBarriers(): Promise<void> {
    for (const barrier of this.barriers) {
      await barrier();
    }
  }

  private async runIdentityChangeBarriers(): Promise<void> {
    for (const barrier of this.identityChangeBarriers) {
      await barrier();
    }
  }

  /** The renew round-trip; the source is host-injected per arm (sdk ADR-010, sdk ADR-018). */
  private async renew(): Promise<void> {
    const phase = this.holder.phase;
    if (!isRenewable(phase)) return;
    const heldJwt = bearerOf(phase);
    if (heldJwt === null) {
      throw new Error(
        `sdk.identity: ${phase.phase} is renewable but serves no bearer — ` +
          'the capability table and the phase union disagree',
      );
    }
    if (this.arm === 'daemon') {
      if (!this.config.daemonBearer) {
        throw new Error('sdk.identity: the daemon arm has no bearer fetch configured');
      }
      const epoch = this.signOutEpoch;
      const outcome = daemonRenewal(heldJwt, await this.config.daemonBearer());
      if (outcome.kind === 'unchanged' || this.signOutEpoch !== epoch) {
        return;
      }
      await this.holder.apply(outcome.event);
      return;
    }
    if (!this.config.renewSource) {
      throw new Error('sdk.identity: the browser arm has no renewal source configured');
    }
    if (!this.config.adopt) {
      throw new Error('sdk.identity: the browser arm has no adopt hook — a rotation with nowhere to go is a construction defect');
    }
    const epoch = this.signOutEpoch;
    const fresh = await this.config.renewSource();
    if (fresh === null) {
      throw new Error('sdk.identity: the renewal source produced no credential');
    }
    if (this.signOutEpoch !== epoch) {
      // (sdk ADR-010)
      return;
    }
    await this.config.adopt(fresh);
    await this.holder.apply({
      type: 'CredentialRenewed',
      material: fresh,
      expires_at: expiresAtOfMaterial(fresh),
    });
  }
}

/** The `cause` field of `lifecycle.logout_unreachable` (sdk ADR-027). */
function causeText(cause: unknown): string {
  if (!(cause instanceof Error)) return String(cause);
  const nested = (cause as { cause?: unknown }).cause;
  const code = (nested as { code?: unknown } | undefined)?.code;
  return typeof code === 'string' ? `${cause.message}: ${code}` : cause.message;
}

function expiresAtOfMaterial(material: CredentialMaterial): number {
  const claims = decodeSessionJwt(material.session_jwt);
  if (claims.exp === null) {
    throw new Error('identity: the session JWT carries no exp claim');
  }
  return claims.exp;
}
